import { useStripe, useElements, CardNumberElement, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js';
import {
    VStack,
    HStack,
    Box,
    FormControl,
    FormLabel,
    Input,
    Button,
    Checkbox,
    Flex,
    Link,
    Text,
} from '@chakra-ui/react';
import React, { useState } from 'react';


type AddPaymentSourceProps = {
    onAdd: (paymentMethodId: string, errors: string[]) => void;
    onCancel: () => void;
};

const stripeElementOptions = {
    style: {
        base: {
            fontSize: '16px',
            color: '#424770',
            '::placeholder': {
                color: '#aab7c4',
            },
            padding: '10px 12px',
        },
        invalid: {
            color: '#9e2146',
        },
    },
};

const AddPaymentSource = ({ onAdd, onCancel }: AddPaymentSourceProps) => {
    const stripe = useStripe();
    const elements = useElements();


    const [nameOnCard, setNameOnCard] = useState('');
    const [billingZip, setBillingZip] = useState('');
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [stripeElementError, setStripeElementError] = useState<string | null>(null);
    const [formError, setFormError] = useState<string | null>(null);

    const handleStripeElementChange = (event: any) => {
        if (event.error) {
            setStripeElementError(event.error.message);
        } else {
            setStripeElementError(null);
        }
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsProcessing(true);
        setStripeElementError(null);
        setFormError(null);

        if (!stripe || !elements) {
            setFormError('Stripe is not initialized. Try reloading the page.');
            setIsProcessing(false);
            return;
        }

        if (!agreedToTerms) {
            setFormError('Please agree to the terms and conditions.');
            setIsProcessing(false);
            return;
        }

        const cardNumberElement = elements.getElement(CardNumberElement);
        if (!cardNumberElement) {
            setFormError('Card details are not ready.');
            setIsProcessing(false);
            return;
        }

        try {
            const { error, paymentMethod } = await stripe.createPaymentMethod({
                type: 'card',
                card: cardNumberElement,
                billing_details: {
                    name: nameOnCard,
                    address: { postal_code: billingZip },
                },
            });
            if (error) throw error;
            if (!paymentMethod) throw new Error('Failed to create payment method.');
            await onAdd(paymentMethod.id, []);
        } catch (error: any) {
            console.log(error);
            const errorMessage = error?.message || 'An unexpected error occurred.';

            setFormError(errorMessage);
            setStripeElementError(errorMessage);
            await onAdd('', [errorMessage]);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <VStack spacing='5' align='stretch'>
                <FormControl isRequired>
                    <FormLabel fontSize='sm'>Name on card</FormLabel>
                    <Input placeholder='Full name' value={nameOnCard} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNameOnCard(e.target.value)} />
                </FormControl>
                <FormControl isRequired isInvalid={!!stripeElementError}>
                    <FormLabel fontSize='sm'>Card Number</FormLabel>
                    <Box border='1px' borderColor='inherit' borderRadius='md' p='2.5'>
                        <CardNumberElement options={stripeElementOptions} onChange={handleStripeElementChange} />
                    </Box>
                </FormControl>
                <HStack spacing='4' align='flex-start'>
                    <FormControl isRequired flex='1'>
                        <FormLabel fontSize='sm'>Expiry Date</FormLabel>
                        <Box border='1px' borderColor='inherit' borderRadius='md' p='2.5'>
                            <CardExpiryElement options={stripeElementOptions} onChange={handleStripeElementChange} />
                        </Box>
                    </FormControl>
                    <FormControl isRequired flex='1'>
                        <FormLabel fontSize='sm'>CVC</FormLabel>
                        <Box border='1px' borderColor='inherit' borderRadius='md' p='2.5'>
                            <CardCvcElement options={stripeElementOptions} onChange={handleStripeElementChange} />
                        </Box>
                    </FormControl>
                    <FormControl isRequired flex='1'>
                        <FormLabel fontSize='sm'>Billing Zip</FormLabel>
                        <Input placeholder='ZIP / Postal' value={billingZip} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBillingZip(e.target.value)} />
                    </FormControl>
                </HStack>
                {stripeElementError && (
                    <Box color='red.500' fontSize='sm' mt={-2}>
                        {stripeElementError}
                    </Box>
                )}
                {formError && (
                    <Text color='red.500' fontSize='sm' mt={-2}>
                        {formError}
                    </Text>
                )}
                <Checkbox isChecked={agreedToTerms} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAgreedToTerms(e.target.checked)} isRequired>
                    I agree to Dialzara LLC's{' '}
                    <Link href='https://dialzara.com/terms-of-use/' isExternal color='blue.500'>
                        terms of use
                    </Link>{' '}and{' '}
                    <Link href='https://dialzara.com/privacy-policy/' isExternal color='blue.500'>
                        privacy policy
                    </Link>
                </Checkbox>
                <Flex justifyContent='space-between' mt='4'>
                    <Button onClick={onCancel} variant='outline' fontSize='sm'>Cancel</Button>
                    <Button type='submit' isLoading={isProcessing} isDisabled={!stripe || !elements || isProcessing} variant='solid' colorScheme='blue'>Add</Button>
                </Flex>
            </VStack>
        </form>
    );
};

export default AddPaymentSource;
